import { P, match } from "ts-pattern";
import { separateLocale } from "../..";

interface DefaultFormatProps {
	lang: string;
	digits?: { min?: number; max?: number };
}

type UnitProps =
	| { style?: "decimal" | "percent" | "decimal-hyphen"; unit?: never; currency?: never }
	| { style?: "unit"; unit: "meter" | "centimeter" | "millimeter" | "degree"; currency?: never }
	| { style?: "currency"; unit?: never; currency?: string };

export type FormatProps = DefaultFormatProps & UnitProps;

export function formatNumber(props: FormatProps & { value: number }) {
	const { lang, digits, value } = props;
	const { country } = separateLocale(lang);
	const style = props.style ?? (props.currency ? "currency" : props.unit ? "unit" : "decimal");
	const unit = style === "unit" ? (props.unit ?? "centimeter") : undefined;

	return match(style)
		.with("decimal-hyphen", () => {
			const formattedValue = Intl.NumberFormat(country, {
				style: "decimal",
				minimumFractionDigits: digits?.min ?? 1,
				maximumFractionDigits: digits?.max ?? 2,
			})
				.format(value)
				.replace(/\u202f/g, "\u00a0");

			const separator = Intl.NumberFormat(country).format(0.1).charAt(1);
			const [integer, decimal] = formattedValue.split(separator);
			const isDecimalEmpty = !decimal || !!decimal.match(/^0+$/);
			const formattedDecimal = isDecimalEmpty ? "-" : decimal;

			return [integer, formattedDecimal].join(separator);
		})
		.with("decimal", "percent", "unit", (s) => {
			return Intl.NumberFormat(lang, {
				style: s,
				unit,
				minimumFractionDigits: digits?.min ?? 1,
				maximumFractionDigits: digits?.max ?? 2,
			})
				.format(value)
				.replace(/\u202f/g, "\u00a0");
		})
		.with("currency", () => {
			return Intl.NumberFormat(country, {
				style: "currency",
				currency: props.currency ?? "EUR",
				minimumFractionDigits: digits?.min ?? 2,
				maximumFractionDigits: digits?.max ?? 2,
			})
				.format(value)
				.replace(/\u202f/g, "\u00a0");
		})
		.run();
}

export function formatDimensions(
	props: Pick<FormatProps, "lang" | "unit" | "digits"> & { values: number[] },
) {
	const { lang, unit, digits, values } = props;

	return values
		.map((value, index) =>
			index !== values.length - 1
				? `${formatNumber({ lang, value: value, style: "decimal", digits })} x `
				: formatNumber({ lang, value: value, style: "unit", unit: unit ?? "centimeter", digits }),
		)
		.join("");
}

export function formatMinMix(props: FormatProps & { min: number; max: number }) {
	const { lang, min, max } = props;

	if (min === 0) {
		return `<= ${formatNumber({ lang, value: max, style: "unit", unit: "centimeter" })}`;
	}

	if (max === 0) {
		return `> ${formatNumber({ lang, value: min, style: "unit", unit: "centimeter" })}`;
	}

	return formatDimensions({ lang, values: [min, max] });
}

export function formatStringToNumber(value?: string) {
	return value ? Number(value.replace(/,/g, ".")) : 0;
}

function getTodaysTime(date?: Date) {
	const today = date ?? new Date();
	const time = today.getTime();
	return time - (time % 86400000);
}

export function getDifferenceInDays(date: Date, today?: Date) {
	const diffTime = date.getTime() - getTodaysTime(today);
	return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
}

interface FormatRelativeDateProps {
	lang: string;
	date: string;
	uppercase?: boolean;
	showAll?: boolean;
}

export function formatRelativeDate(props: FormatRelativeDateProps) {
	const { lang, date, uppercase = false, showAll = true } = props;
	const today = new Date();
	const newDate = new Date(date);
	const diffDays = getDifferenceInDays(newDate, today);

	const weekday = newDate.toLocaleDateString(lang, { weekday: "long" });
	const dayAndMonth = newDate.toLocaleDateString(lang, { day: "numeric", month: "long" });
	const formatter = new Intl.RelativeTimeFormat(lang, { numeric: "auto", style: "long" });
	const formattedInDays = formatter.format(diffDays, "day");
	const formattedInWeeks = formatter.format(Math.floor(diffDays / 7), "week");

	const nextSaturday = new Date(today);
	nextSaturday.setDate(today.getDate() + (6 - today.getDay()));
	const isThisWeek =
		(today.valueOf() === nextSaturday.valueOf() && diffDays < 7) || newDate <= nextSaturday;

	const formattedDate = showAll
		? match(diffDays)
				.with(-2, -1, 0, 1, 2, () => formattedInDays)
				.with(
					P.when((t) => t > 2 && t < 14),
					() => `${formattedInWeeks} ${weekday} ${dayAndMonth}`,
				)
				.with(
					P.when((t) => t >= 14),
					() => `${weekday} ${dayAndMonth} ${formattedInWeeks}`,
				)
				.otherwise(() => undefined)
		: isThisWeek
			? match(diffDays)
					.with(-2, -1, 0, 1, 2, () => formattedInDays)
					.with(
						P.when((t) => t > 2 && t < 7),
						() => weekday,
					)
					.with(
						P.when((t) => t >= 7 && t < 14),
						() => `${formattedInWeeks} ${weekday} ${dayAndMonth}`,
					)
					.with(
						P.when((t) => t >= 14),
						() => `${weekday} ${dayAndMonth} ${formattedInWeeks}`,
					)
					.otherwise(() => undefined)
			: undefined;

	if (formattedDate) {
		if (uppercase) {
			// first letter to uppercase
			return formattedDate.charAt(0).toUpperCase() + formattedDate.slice(1);
		}

		return formattedDate;
	}
}
